import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiTrash } from "react-icons/bi";
import { MdOutlineSchema } from "react-icons/md";
import { useNavigate } from "react-router";
import cuid from "cuid";
import { defaultsDeep } from "lodash";
import Header from "shared/components/Header";
import Tabs from "shared/components/Tab";
import { NAME_SPACES } from "shared/locales/constants";
import { PROCESS_CONFIG_TYPES, SCREENS } from "utils/constants";
import { serializeProcessProducts } from "../productConfig";
import { sortProcessStatuses } from "../sortProcessStatuses";
import {
  AdditionalConfiguration,
  General,
  Needs,
  PossibleStatuses,
  Skills,
  Statuses,
} from "./tabs";

const TABS = {
  GENERAL: "general",
  STATUSES: "statuses",
  POSSIBLE_STATUSES: "possibleStatuses",
  SKILLS: "skills",
  NEEDS: "needs",
  ADDITIONAL_CONFIG: "additionalConfig",
};

const Detail = ({ onSubmit, data, cancel }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const [modal, setModal] = useState({
    visible: false,
  });
  const [activeTab, setActiveTab] = useState(TABS.GENERAL);
  const generalFormRef = useRef(null);
  const statusesFormRef = useRef(null);
  const possibleStatusesFormRef = useRef(null);
  const skillsFormRef = useRef(null);
  const needsFormRef = useRef(null);
  const additionalConfigFormRef = useRef(null);
  const [isDirty, setIsDirty] = useState({
    [TABS.GENERAL]: false,
    [TABS.STATUSES]: false,
    [TABS.POSSIBLE_STATUSES]: false,
    [TABS.SKILLS]: false,
    [TABS.NEEDS]: false,
    [TABS.ADDITIONAL_CONFIG]: false,
  });
  const generalDirtyRef = useRef(false);
  const statusesDirtyRef = useRef(false);
  const possibleStatusesDirtyRef = useRef(false);
  const skillsDirtyRef = useRef(false);
  const needsDirtyRef = useRef(false);
  const additionalConfigDirtyRef = useRef(false);
  const {
    name,
    type,
    theme,
    processStatuses: initialProcessStatuses,
    skills,
    config,
  } = data;

  // Shared state for processStatuses (synced between Statuses and PossibleStatuses tabs)
  const [sharedProcessStatuses, setSharedProcessStatuses] = useState(
    sortProcessStatuses(initialProcessStatuses || []),
  );
  const navigate = useNavigate();
  const DETAIL = t("DETAIL", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });

  const tabsConfig = [
    { key: TABS.GENERAL, label: FORM.GENERAL_INFO },
    { key: TABS.STATUSES, label: FORM.STATUSES_INFO },
    {
      key: TABS.POSSIBLE_STATUSES,
      label: FORM.POSSIBLE_STATUSES.POSSIBLE_STATUSES_INFO,
    },
    { key: TABS.SKILLS, label: FORM.SKILLS.TITLE },
    {
      key: TABS.NEEDS,
      label: FORM.NEEDS_CONFIG || "Configuration des besoins",
    },
    {
      key: TABS.ADDITIONAL_CONFIG,
      label: FORM.ADDITIONAL_CONFIG || "Configuration Additionnel",
    },
  ];

  const handleSave = async () => {
    // Collect values from all form instances
    const generalValues = generalFormRef.current?.values || {};
    const skillsValues = skillsFormRef.current?.values || {};
    const needsValues = needsFormRef.current?.values || {};
    const additionalConfigValues =
      additionalConfigFormRef.current?.values || {};

    // Get status data from both forms:
    // - Statuses tab has the latest status info (name, tag, order, template, etc.)
    // - PossibleStatuses tab has possibleStatuses and actions
    const statusesFormData =
      statusesFormRef.current?.values?.processStatuses || [];
    const possibleStatusesFormData =
      possibleStatusesFormRef.current?.values?.processStatuses || [];

    // Create a map of possibleStatuses/actions by status id from PossibleStatuses form
    const possibleStatusesMap = {};
    possibleStatusesFormData.forEach((status) => {
      possibleStatusesMap[status.id] = {
        possibleStatuses: status.possibleStatuses || [],
        actions: status.actions || [],
      };
    });

    // Merge: take status info from Statuses tab, possibleStatuses/actions from PossibleStatuses tab
    const baseStatuses =
      statusesFormData.length > 0
        ? statusesFormData
        : sharedProcessStatuses.length > 0
          ? sharedProcessStatuses
          : data.processStatuses || [];

    const processStatusesData = sortProcessStatuses(
      baseStatuses.map((status) => ({
        ...status,
        // Preserve possibleStatuses from PossibleStatuses form, or keep existing
        possibleStatuses:
          possibleStatusesMap[status.id]?.possibleStatuses ||
          status.possibleStatuses ||
          [],
        // Preserve actions from PossibleStatuses form, or keep existing
        actions:
          possibleStatusesMap[status.id]?.actions || status.actions || [],
      })),
    );

    // Build the merged payload following the pattern from each tab's onSubmit
    const payload = {
      // From General tab
      name: generalValues.name || data.name,
      type: generalValues.type || data.type,
      theme: generalValues.theme || data.theme,
      additionalInfo: {
        ...data.additionalInfo,
        sessionReplay:
          additionalConfigValues.sessionReplay ??
          data.additionalInfo?.sessionReplay,
      },
      description: generalValues.description || data.description || "",

      // From Statuses/PossibleStatuses tabs (merged)
      processStatuses: processStatusesData,

      // From Skills tab
      skills: skillsValues.skills || data.skills,
      attributionType: defaultsDeep(
        skillsValues.attributionType ||
          config.find(
            ({ type }) => type === PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
          ),
        {
          id: cuid(),
          type: PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
          value: null,
        },
      ),

      // From Needs tab and Additional Configuration tab
      config: (() => {
        // Start with existing config, excluding NEEDS, ATTRIBUTION_TYPE, PREVIOUS_ADVISOR_CONFIG, and PRODUCT
        const baseConfig = config.filter(
          ({ type }) =>
            type !== PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE &&
            type !== PROCESS_CONFIG_TYPES.PREVIOUS_ADVISOR_CONFIG &&
            type !== PROCESS_CONFIG_TYPES.NEEDS &&
            type !== PROCESS_CONFIG_TYPES.PRODUCT &&
            type !== PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
        );

        // Add attributionType if it has a value
        const attributionTypeConfig = skillsValues.attributionType?.value
          ? [skillsValues.attributionType]
          : [];

        // Add previousAdvisorConfig from Skills tab (always include so it is saved)
        const previousAdvisorConfig = skillsValues.previousAdvisorConfig
          ? [skillsValues.previousAdvisorConfig]
          : [];

        // Build needs config from Needs tab
        const existingNeedsConfig = config.find(
          ({ type }) => type === PROCESS_CONFIG_TYPES.NEEDS,
        );
        let needsConfig = [];

        if (
          needsValues.needsLevels !== undefined ||
          needsValues.selectedCategories?.length > 0
        ) {
          // Build categories object with only selected categories
          const categoriesData = {};
          if (needsValues.selectedCategories?.length > 0) {
            needsValues.selectedCategories.forEach((categoryId) => {
              categoriesData[categoryId] = {};
              // If personalizeText is checked, use the values from form, otherwise empty strings
              if (
                needsValues.personalizeText &&
                needsValues.categories?.[categoryId]
              ) {
                categoriesData[categoryId] = {
                  ...needsValues.categories[categoryId],
                };
              }
              // Ensure all levels are present (only if needsLevels is set)
              if (needsValues.needsLevels) {
                for (let i = 1; i <= needsValues.needsLevels; i++) {
                  if (!categoriesData[categoryId][String(i)]) {
                    categoriesData[categoryId][String(i)] = "";
                  }
                }
              }
            });
          }

          // Build labels object
          const labelsData = {};
          if (needsValues.needsLevels && needsValues.labels) {
            for (let i = 1; i <= needsValues.needsLevels; i++) {
              labelsData[String(i)] = needsValues.labels[String(i)] || "";
            }
          }

          needsConfig = [
            {
              id: existingNeedsConfig?.id || cuid(),
              type: PROCESS_CONFIG_TYPES.NEEDS,
              value: {
                needsLevels: needsValues.needsLevels ?? null,
                categories: categoriesData,
                labels: labelsData,
              },
            },
          ];
        } else if (existingNeedsConfig) {
          // Keep existing needs config if no changes
          needsConfig = [existingNeedsConfig];
        }

        // Build product config from Additional Configuration tab
        const existingProductConfig = config.find(
          ({ type }) => type === PROCESS_CONFIG_TYPES.PRODUCT,
        );
        let productConfig = [];

        if (additionalConfigValues.products !== undefined) {
          productConfig = [
            {
              id: existingProductConfig?.id || cuid(),
              type: PROCESS_CONFIG_TYPES.PRODUCT,
              value: serializeProcessProducts(additionalConfigValues.products),
            },
          ];
        } else if (existingProductConfig) {
          productConfig = [existingProductConfig];
        }

        const existingAssociatedConfig = config.find(
          ({ type }) => type === PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
        );
        let associatedProcessesConfig = [];

        if (additionalConfigValues.associatedProcessesValue !== undefined) {
          associatedProcessesConfig = [
            {
              id: existingAssociatedConfig?.id || cuid(),
              type: PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
              value: additionalConfigValues.associatedProcessesValue,
            },
          ];
        } else if (existingAssociatedConfig) {
          associatedProcessesConfig = [existingAssociatedConfig];
        }

        return [
          ...baseConfig,
          ...attributionTypeConfig,
          ...previousAdvisorConfig,
          ...needsConfig,
          ...productConfig,
          ...associatedProcessesConfig,
        ];
      })(),

      // Other fields
      insurancePlans: data.insurancePlans?.map(({ id }) => id),
    };

    // Submit the merged payload
    const saved = await onSubmit(payload);
    if (saved === false) return;

    const formRefs = [
      [generalFormRef, generalDirtyRef],
      [statusesFormRef, statusesDirtyRef],
      [possibleStatusesFormRef, possibleStatusesDirtyRef],
      [skillsFormRef, skillsDirtyRef],
      [needsFormRef, needsDirtyRef],
      [additionalConfigFormRef, additionalConfigDirtyRef],
    ];
    formRefs.forEach(([formRef, dirtyRef]) => {
      if (formRef.current) {
        formRef.current.resetForm({ values: formRef.current.values });
      }
      dirtyRef.current = false;
    });

    // Reset dirty state for all tabs
    setIsDirty({
      [TABS.GENERAL]: false,
      [TABS.STATUSES]: false,
      [TABS.POSSIBLE_STATUSES]: false,
      [TABS.SKILLS]: false,
      [TABS.NEEDS]: false,
      [TABS.ADDITIONAL_CONFIG]: false,
    });
  };

  const isSaveDisabled = !Object.values(isDirty).some(
    (dirty) => dirty === true,
  );

  return (
    <>
      <Header
        title={DETAIL.HEADER.TITLE}
        subtitle={DETAIL.HEADER.ACTION}
        actions={[
          {
            text: DETAIL.HEADER.DISCARD,
            onClick: cancel,
            type: "primary--outlined",
            icon: <BiChevronLeft size={20} />,
          },
          {
            text: DETAIL.HEADER.DELETE,
            onClick: () =>
              navigate(
                `${SCREENS.PRIVATE.ADMIN.PROCESSES.DELETE.path}/${data.id}`,
              ),
            type: "danger--outlined",
            icon: <BiTrash size={20} />,
          },
          {
            text: FORM.CANVAS || "Canvas",
            onClick: () =>
              navigate(`${SCREENS.PRIVATE.ADMIN.CANVASES.path}/${data.id}`),
            type: "primary--outlined",
            icon: <MdOutlineSchema size={20} />,
          },
          {
            text: DETAIL.HEADER.SAVE,
            onClick: handleSave,
            type: "primary",
            icon: <BiCheck size={20} />,
            disabled: isSaveDisabled,
          },
        ]}
      />
      <Tabs
        variant="admin"
        tabsConfig={tabsConfig}
        activeTab={activeTab}
        onChange={setActiveTab}
      >
        <div style={{ display: activeTab === TABS.GENERAL ? "block" : "none" }}>
          <General
            formRef={generalFormRef}
            dirtyRef={generalDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            data={data}
            onSubmit={onSubmit}
            type={type}
            name={name}
            theme={theme}
            config={config}
          />
        </div>
        <div
          style={{ display: activeTab === TABS.STATUSES ? "block" : "none" }}
        >
          <Statuses
            formRef={statusesFormRef}
            dirtyRef={statusesDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            data={data}
            onSubmit={onSubmit}
            processStatuses={sharedProcessStatuses}
            config={config}
            onProcessStatusesChange={setSharedProcessStatuses}
          />
        </div>
        <div
          style={{
            display: activeTab === TABS.POSSIBLE_STATUSES ? "block" : "none",
          }}
        >
          <PossibleStatuses
            formRef={possibleStatusesFormRef}
            dirtyRef={possibleStatusesDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            data={data}
            onSubmit={onSubmit}
            processStatuses={sharedProcessStatuses}
            config={config}
            modal={modal}
            setModal={setModal}
          />
        </div>
        <div style={{ display: activeTab === TABS.SKILLS ? "block" : "none" }}>
          <Skills
            formRef={skillsFormRef}
            dirtyRef={skillsDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            data={data}
            onSubmit={onSubmit}
            skills={skills}
            config={config}
          />
        </div>
        <div style={{ display: activeTab === TABS.NEEDS ? "block" : "none" }}>
          <Needs
            formRef={needsFormRef}
            dirtyRef={needsDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            data={data}
            onSubmit={onSubmit}
            config={config}
          />
        </div>
        <div
          style={{
            display: activeTab === TABS.ADDITIONAL_CONFIG ? "block" : "none",
          }}
        >
          <AdditionalConfiguration
            formRef={additionalConfigFormRef}
            dirtyRef={additionalConfigDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            data={data}
            onSubmit={onSubmit}
            config={config}
          />
        </div>
      </Tabs>
    </>
  );
};

export default Detail;
